import React from 'react';
import { connect } from 'react-redux';

import { filter, find, orderBy } from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';

import {
  HamburgerMenu,
  NavigationBox,
  ScrollAwareNavigationBox,
  StickySideNav,
} from '@opendoor/bricks';
import OperatorInfoBox from '../../components/common/OperatorInfoBox';
import BRNDocumentsSection from '../../components/resaleClosingDashboard/BRNDocumentsSection';
import ChangeCloseDateModal from '../../components/resaleClosingDashboard/ChangeCloseDateModal';
import CloseDateNextStep from '../../components/resaleClosingDashboard/CloseDateNextStep';
import ContractDocumentsSection from '../../components/resaleClosingDashboard/ContractDocumentsSection';
import ListingCard from '../../components/resaleClosingDashboard/ListingCard';
import SellerResponseSection from '../../components/resaleClosingDashboard/SellerResponseSection';
import TransactionFactsSection from '../../components/resaleClosingDashboard/TransactionFactsSection';
import TransactionPartiesSection from '../../components/resaleClosingDashboard/TransactionPartiesSection';
import WorkOrderSection from '../../components/resaleClosingDashboard/WorkOrderSection';
import {
  BINSR_FINAL_TYPE,
  BRN_CREDIT_ADDENDUM_TYPE,
  DOCUMENT_CHECKLIST_ITEM_STATES,
} from '../../constants/DocumentChecklistItemConstants';
import { LISTING_OFFER_CHANNELS } from '../../constants/ListingOfferConstants';
import {
  EventTrackingFn,
  IDocumentChecklistItem,
  IListingOffer,
  IRequestStatus,
  IRequestStatusMap,
  RepairPreference,
} from '../../declarations';
import layoutWrapper from '../../hocs/layoutWrapper';
import resaleClosingDashboardLoader from '../../hocs/resaleClosingDashboardLoader';
import Responsive from '../../hocs/Responsive';
import * as resaleClosingDashboardActions from '../../store/resaleClosingDashboard/actions';
import * as resaleClosingDashboardSelectors from '../../store/resaleClosingDashboard/selectors';
import './ResaleClosingDashboardStyle.less';

const NAV_LINK_IDS = [
  { idToWatch: 'facts-section', text: 'Transaction Summary' },
  { idToWatch: 'parties-section', text: 'Contact Information' },
  { idToWatch: 'documents-section', text: 'Transaction Documents' },
  { idToWatch: 'repair-documents-section', text: 'Repair Documents' },
  { idToWatch: 'seller-response-section', text: 'Seller Response' },
  { idToWatch: 'work-order-section', text: 'Work Orders' },
];

export interface IResaleClosingDashboardProps {
  brnAdditionalDocumentTypes: string[];
  brnDocumentChecklistItems: IDocumentChecklistItem[];
  contractAdditionalDocumentTypes: string[];
  contractDocumentChecklistItems: IDocumentChecklistItem[];
  downloadChecklistDocument: (documentId: number) => void;
  generateSellerResponseRequestStatus: IRequestStatus | undefined;
  listingOffer: IListingOffer;
  match: { params: { listingOfferId: string } };
  removeChecklistDocument: (documentId: number) => void;
  removeChecklistDocumentRequests: IRequestStatusMap;
  requestGenerateSellerResponse: () => void;
  submitBuyerRepairPreferences: (
    notes: string | null,
    preferredSellerReponse: RepairPreference,
  ) => void;
  submitChangeClosingDateRequest: (newDate: moment.Moment, reason: string) => void;
  trackEvent: EventTrackingFn;
  uploadChecklistDocument: (
    name: string,
    documentType: string,
    fileBase64DataUrl: string,
    requestId: number,
    checklistItemId?: number,
  ) => void;
  uploadChecklistDocumentRequests: IRequestStatusMap;
}

interface IResaleClosingDashboardState {
  isChangeCloseDateModalOpen: boolean;
  isMobileNavVisible: boolean;
}

export class ResaleClosingDashboard extends React.PureComponent<
  IResaleClosingDashboardProps,
  IResaleClosingDashboardState
> {
  public state = {
    isChangeCloseDateModalOpen: false,
    isMobileNavVisible: false,
  };

  private MOBILE_NAV_CONFIG = [
    {
      active: false,
      onClick: this.navigateToIdAndToggleNav.bind(this, 'facts-section'),
      text: 'Transaction Summary',
    },
    {
      active: false,
      onClick: this.navigateToIdAndToggleNav.bind(this, 'parties-section'),
      text: 'Contact Information',
    },
    {
      active: false,
      onClick: this.navigateToIdAndToggleNav.bind(this, 'documents-section'),
      text: 'Transaction Documents',
    },
    {
      active: false,
      onClick: this.navigateToIdAndToggleNav.bind(this, 'repair-documents-section'),
      text: 'Repair Documents',
    },
  ];

  public componentDidMount() {
    const { listingOffer, trackEvent } = this.props;
    const { listingOfferId } = this.props.match.params;
    const { channel } = listingOffer;

    trackEvent('resale-closing-dashboard', 'load', 'dashboard', { channel, listingOfferId });
  }

  public render(): JSX.Element {
    const {
      brnAdditionalDocumentTypes,
      brnDocumentChecklistItems,
      contractAdditionalDocumentTypes,
      contractDocumentChecklistItems,
      downloadChecklistDocument,
      generateSellerResponseRequestStatus,
      listingOffer,
      removeChecklistDocument,
      removeChecklistDocumentRequests,
      requestGenerateSellerResponse,
      submitBuyerRepairPreferences,
      uploadChecklistDocument,
      uploadChecklistDocumentRequests,
    } = this.props;

    const { isChangeCloseDateModalOpen } = this.state;

    const { brn, listing } = listingOffer;
    const resaleTCLead = listing.resale_tc_lead;

    return (
      <div className="resale-closing-dashboard">
        <div className="container">
          <div className="dashboard-heading hidden-sm-down">Closing dashboard</div>
          <div className="row">
            <div className="col-xs-12 col-md-3">
              <Responsive render={this.renderNav} />
            </div>
            <div className="col-md-8 col-md-offset-1">
              <div className="greeting">
                {listingOffer.channel === LISTING_OFFER_CHANNELS.direct
                  ? 'Congratulations, we are now in contract! We will manage and upload all the paperwork for ' +
                    'you. Use this dashboard to track and view your closing information all in one place.'
                  : 'Congratulations, we are now in contract! You can see closing information ' +
                    'regarding your transaction.'}
              </div>
              {this.maybeRenderCloseDateNextStep()}
              <TransactionFactsSection
                changeClosingDateRequest={this.latestClosingDateRequest()}
                closeOfEscrowDate={listingOffer.close_of_escrow}
                earnestDepositCents={listingOffer.earnest_deposit_cents}
                openChangeClosingDateModal={this.openChangeClosingDateModal}
                pendingDate={listing.pending_date}
              />
              <TransactionPartiesSection
                channel={listingOffer.channel}
                ownerEntityName={listingOffer.owner_entity_name}
                resaleAgentLead={listing.resale_agent_lead}
                resaleTCLead={resaleTCLead}
                resaleTitleContact={listingOffer.resale_title_contact}
                resaleTitleOrganization={listingOffer.resale_title_organization}
              />
              <ContractDocumentsSection
                additionalDocumentTypes={contractAdditionalDocumentTypes}
                channel={listingOffer.channel}
                documentChecklistItems={contractDocumentChecklistItems}
                onDocumentDownload={downloadChecklistDocument}
                onDocumentRemove={removeChecklistDocument}
                onDocumentSave={uploadChecklistDocument}
                removeChecklistDocumentRequests={removeChecklistDocumentRequests}
                uploadChecklistDocumentRequests={uploadChecklistDocumentRequests}
              />
              <BRNDocumentsSection
                accessInfo={listingOffer.access_info}
                additionalDocumentTypes={brnAdditionalDocumentTypes}
                buyerRepairPreference={listingOffer.buyer_repair_preference}
                channel={listingOffer.channel}
                documentChecklistItems={brnDocumentChecklistItems}
                onDocumentDownload={downloadChecklistDocument}
                onDocumentRemove={removeChecklistDocument}
                onDocumentSave={uploadChecklistDocument}
                removeChecklistDocumentRequests={removeChecklistDocumentRequests}
                repairPreferences={listingOffer.buyer_repair_preference}
                submitBuyerRepairPreferences={submitBuyerRepairPreferences}
                supportPhoneNumber={listing.market_default_phone}
                uploadChecklistDocumentRequests={uploadChecklistDocumentRequests}
              />
              <SellerResponseSection
                brn={brn}
                canGenerateSellerResponse={listingOffer.can_generate_seller_repair_response}
                channel={listingOffer.channel}
                creditAddendumItem={brnDocumentChecklistItems.find(
                  checklistItem => checklistItem.document_type === BRN_CREDIT_ADDENDUM_TYPE,
                )}
                generateSellerResponseRequestStatus={generateSellerResponseRequestStatus}
                onDocumentDownload={downloadChecklistDocument}
                onDocumentRemove={removeChecklistDocument}
                onDocumentSave={uploadChecklistDocument}
                onRequestGenerateSellerResponse={requestGenerateSellerResponse}
                removeChecklistDocumentRequests={removeChecklistDocumentRequests}
                resaleAgentLead={listing.resale_agent_lead}
                sellerResponseItem={brnDocumentChecklistItems.find(
                  checklistItem => checklistItem.document_type === BINSR_FINAL_TYPE,
                )}
                uploadChecklistDocumentRequests={uploadChecklistDocumentRequests}
              />
              <WorkOrderSection workOrders={brn && brn.work_orders} />
            </div>
          </div>
        </div>
        <ChangeCloseDateModal
          isOpen={isChangeCloseDateModalOpen}
          onRequestClose={this.closeChangeClosingDateModal}
          onSubmit={this.handleChangeCloseDate}
        />
      </div>
    );
  }

  private maybeRenderCloseDateNextStep = () => {
    const closeDateChecklistItem = this.actionableCloseDateChecklistItem();
    if (closeDateChecklistItem) {
      return (
        <CloseDateNextStep
          coeDocumentChecklistItem={closeDateChecklistItem}
          onDocumentDownload={this.props.downloadChecklistDocument}
          onDocumentRemove={this.props.removeChecklistDocument}
          onDocumentSave={this.props.uploadChecklistDocument}
          removeChecklistDocumentRequests={this.props.removeChecklistDocumentRequests}
          uploadChecklistDocumentRequests={this.props.uploadChecklistDocumentRequests}
        />
      );
    }
  };

  private actionableCloseDateChecklistItem = () => {
    const latestClosingDateRequest = this.latestClosingDateRequest();
    if (!latestClosingDateRequest || latestClosingDateRequest.state !== 'accepted') {
      return;
    }
    return find(this.props.contractDocumentChecklistItems, item => {
      return (
        item.itemable_type === 'ResaleAddendum' &&
        item.itemable_id === latestClosingDateRequest.response_metadata['resale_addendum_id'] &&
        (item.state === DOCUMENT_CHECKLIST_ITEM_STATES.initial ||
          item.state === DOCUMENT_CHECKLIST_ITEM_STATES.needs_external_signature)
      );
    });
  };

  private latestClosingDateRequest = () => {
    const { listingOffer } = this.props;
    const coeRequests = filter(listingOffer.customer_requests, {
      request_type: 'resale_coe_date_change',
    });
    return orderBy(coeRequests, ['id'], ['desc'])[0];
  };

  private handleChangeCloseDate = (newDate, reason) => {
    this.props.submitChangeClosingDateRequest(newDate, reason);
    this.setState({ isChangeCloseDateModalOpen: false });
  };

  private closeChangeClosingDateModal = () => {
    this.setState({ isChangeCloseDateModalOpen: false });
  };

  private openChangeClosingDateModal = () => {
    this.setState({ isChangeCloseDateModalOpen: true });
  };

  private showMobileNav = () => {
    this.setState({ isMobileNavVisible: true });
  };

  private hideMobileNav = () => {
    this.setState({ isMobileNavVisible: false });
  };

  private navigateToIdAndToggleNav(hash) {
    this.hideMobileNav();
    window.location.hash = '';
    window.location.hash = hash;
  }

  private renderNav = ({ gtMD }) => {
    const { listingOffer } = this.props;
    const { listing } = listingOffer;
    const resaleTCLead = listing.resale_tc_lead;

    if (gtMD) {
      return (
        <StickySideNav>
          <div className="dashboard-nav">
            <ScrollAwareNavigationBox links={NAV_LINK_IDS} />
            <div className="left-column-divider" />
            <ListingCard listing={listing} />
            <div className="left-column-divider no-margin-top" />
            <OperatorInfoBox
              compact={true}
              email={resaleTCLead.external_email || resaleTCLead.email}
              fullName={resaleTCLead.full_name}
              operatorRole="Closing Manager"
              phoneNumber={resaleTCLead.phone_number}
              photoUrl={resaleTCLead.image}
            />
          </div>
        </StickySideNav>
      );
    } else {
      return (
        <HamburgerMenu
          disableBodyScrollOnActive={true}
          onClose={this.hideMobileNav}
          onOpen={this.showMobileNav}
          showMenu={this.state.isMobileNavVisible}
          title="Closing dashboard"
        >
          <NavigationBox links={this.MOBILE_NAV_CONFIG} />
        </HamburgerMenu>
      );
    }
  };
}

const mapStateToProps = createSelector(
  [
    resaleClosingDashboardSelectors.getBrnAdditionalDocumentTypes(),
    resaleClosingDashboardSelectors.getBrnDocumentChecklistItems(),
    resaleClosingDashboardSelectors.getContractAdditionalDocumentTypes(),
    resaleClosingDashboardSelectors.getContractDocumentChecklistItems(),
    resaleClosingDashboardSelectors.generateSellerResponseRequestStatus,
    resaleClosingDashboardSelectors.listingOffer,
    resaleClosingDashboardSelectors.removeChecklistDocumentRequests,
    resaleClosingDashboardSelectors.uploadChecklistDocumentRequests,
  ],
  (
    brnAdditionalDocumentTypes: string[],
    brnDocumentChecklistItems: IDocumentChecklistItem[],
    contractAdditionalDocumentTypes: string[],
    contractDocumentChecklistItems: IDocumentChecklistItem[],
    generateSellerResponseRequestStatus: IRequestStatus,
    listingOffer: IListingOffer,
    removeChecklistDocumentRequests: IRequestStatusMap,
    uploadChecklistDocumentRequests: IRequestStatusMap,
  ) => ({
    brnAdditionalDocumentTypes,
    brnDocumentChecklistItems,
    contractAdditionalDocumentTypes,
    contractDocumentChecklistItems,
    generateSellerResponseRequestStatus,
    listingOffer,
    removeChecklistDocumentRequests,
    uploadChecklistDocumentRequests,
  }),
);

const mapDispatchToProps = {
  downloadChecklistDocument: resaleClosingDashboardActions.downloadChecklistDocument,
  removeChecklistDocument: resaleClosingDashboardActions.removeChecklistDocument,
  requestGenerateSellerResponse: resaleClosingDashboardActions.requestGenerateSellerResponse,
  submitBuyerRepairPreferences: resaleClosingDashboardActions.submitBuyerRepairPreferences,
  submitChangeClosingDateRequest: resaleClosingDashboardActions.submitChangeClosingDateRequest,
  trackEvent: resaleClosingDashboardActions.trackEvent,
  uploadChecklistDocument: resaleClosingDashboardActions.uploadChecklistDocument,
};

export default layoutWrapper(
  resaleClosingDashboardLoader(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(ResaleClosingDashboard),
  ),
  { footerType: 'FULL' },
);
